/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2023 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { addToChainAfter, executeAwaitableHandlerChain, Handler, replaceInChain } from "@insite/client-framework/HandlerCreator";
import {
    HasOnSuccess,
    markSkipOnCompleteIfOnSuccessIsSet,
} from "@insite/client-framework/HandlerCreator";
import { GetProductCollectionApiV2Parameter, GetRelatedProductCollectionApiV2Parameter } from "@insite/client-framework/Services/ProductServiceV2";
import { chain, LoadProducts, LoadRealTimeInventory } from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/LoadProductInfoList";
import loadProducts from "@insite/client-framework/Store/Data/Products/Handlers/LoadProducts";
import { getProductsDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";

interface Parameter {
    id: string;
    getProductCollectionParameter: GetProductCollectionApiV2Parameter | GetRelatedProductCollectionApiV2Parameter;
    extraProductOptions?: {
        getProductCollectionParameter: GetProductCollectionApiV2Parameter | GetRelatedProductCollectionApiV2Parameter;
        numberOfProductsToDisplay: number;
    };
    forceLoad?: boolean;
    excludeProductId?: string;
    isShellEdit?: boolean;
    additionalSort?: (products: ProductModel[]) => ProductModel[];
}

interface Props {
    products?: ProductModel[];
    productInfos?: ProductInfo[];
    pricingLoaded?: true;
    errorMessage?: string;
    dataViewHasProducts?: boolean;
}

type HandlerType = Handler<Parameter & HasOnSuccess<any>, Props>;

export const ExecuteOnSuccessCallback: HandlerType = props => {
    markSkipOnCompleteIfOnSuccessIsSet(props);
    props.parameter.onSuccess?.(props.products);
};

export const xLoadProducts: HandlerType = async props => {
    const parameter = props.parameter.getProductCollectionParameter;
    if (!parameter) {
        return false;
    }

    // xng-change - https://xngage.atlassian.net/browse/EIS-3792
    (parameter as any).searchHub = (props.parameter as any).searchHub;
    (parameter as any).id = (props.parameter as any).id;

    const productsDataViewState = getProductsDataView(props.getState(), parameter);
    if (productsDataViewState.isLoading) {
        props.dispatch({
            type: "Components/ProductInfoLists/SetWaitingForData",
            id: props.parameter.id,
            parameter: props.parameter.getProductCollectionParameter,
        });
        return false;
    }

    props.dataViewHasProducts = !!productsDataViewState.value;
    if (props.dataViewHasProducts) {
        props.products = [...productsDataViewState.value!];
    }

    if (props.products && !props.parameter.forceLoad) {
        return;
    }

    try {
        props.products = await executeAwaitableHandlerChain(loadProducts, parameter, props);
    } catch (error) {
        props.errorMessage = error;
    }
};

addToChainAfter(chain, LoadRealTimeInventory, ExecuteOnSuccessCallback);
replaceInChain(chain, LoadProducts, xLoadProducts);
